import { useCallback, useEffect, useRef } from 'react'
import { useLazyQuery, useMutation, useQuery, type DefaultContext } from '@apollo/client'
import { type DocumentNode } from 'graphql'

import type {
  LazyQueryHookParams,
  QueryHookParamsWithCustomOptions,
  MutationHookParamsWithCustomOptions,
} from './types'

// Error reporting: the `errorLog` travels through the operation context and
// the Apollo ErrorLink (lib/apollo-client/link.ts) reports every failed
// operation to Datadog — the builders no longer log on their own, so each
// error is reported exactly once.

/**
 * Builds a query hook for the provided queries.
 *
 * @param QUERIES - A record of query names to GraphQL DocumentNodes. @see [graphql folder](../../graphql/brands/index.ts)
 * @returns A function that returns a query hook for the specified query name.
 */
export const buildQueryHook = <
  TNames extends string,
  TVariables extends Record<TNames, Record<string, unknown>>,
  TData extends Record<TNames, unknown>,
>(
  QUERIES: Record<TNames, DocumentNode>
) => {
  return <TQueryName extends TNames>({
    queryName,
    queryVariables,
    errorLog,
    options = {},
  }: QueryHookParamsWithCustomOptions<TQueryName, TVariables[TQueryName], TData[TQueryName]>) => {
    // `errorLog` is merged into the caller's context (never replaced) so
    // options like `context: { headers }` keep working alongside it.
    const { context, ...restOptions } = options

    const { data, loading, called, error, refetch, fetchMore, networkStatus, startPolling, stopPolling } = useQuery<
      TData[TQueryName],
      TVariables[TQueryName]
    >(QUERIES[queryName], {
      notifyOnNetworkStatusChange: true,
      variables: queryVariables as TVariables[TQueryName],
      ...restOptions,
      context: { errorLog, ...context },
    })

    return {
      data,
      loading,
      called,
      error,
      networkStatus,
      refetch,
      fetchMore,
      startPolling,
      stopPolling,
    } as const
  }
}

/**
 * Builds a lazy query hook for the provided queries.
 *
 * @param QUERIES - A record of query names to GraphQL DocumentNodes. @see [graphql folder](../../graphql/brands/index.ts)
 * @returns A function that returns a lazy query hook for the specified query name.
 */
export const buildLazyQueryHook = <
  TNames extends string,
  TVariables extends Record<TNames, Record<string, unknown>>,
  TData extends Record<TNames, unknown>,
>(
  QUERIES: Record<TNames, DocumentNode>
) => {
  /**
   * @remarks This hook allows you to execute a query on demand, rather than automatically when the component mounts.
   * It returns a function to load the query and an object containing the query's data, loading state, error, and other properties.
   *
   * @example
   * const [getBrand, { data, loading, error }] = useBrandsLazyQueries({
   *   queryName: 'getBrand',
   *   queryVariables: { brandId: '123' },
   *   errorLog: GET_BRAND_ERROR_LOG,
   *   options: { fetchPolicy: 'cache-first' },
   * });
   */
  return <TQueryName extends TNames>({
    queryName,
    queryVariables,
    errorLog,
    options = {},
  }: LazyQueryHookParams<TQueryName, TVariables[TQueryName], TData[TQueryName]>) => {
    // `context` is kept out of the hook options: every execution goes through
    // `loadQuery`, which always sends a context — a hook-level one would be
    // silently replaced (Apollo replaces, not merges, context on execute).
    const { context, ...restOptions } = options

    const [execute, { data, loading, called, error, refetch, fetchMore, networkStatus }] = useLazyQuery<
      TData[TQueryName],
      TVariables[TQueryName]
    >(QUERIES[queryName], {
      notifyOnNetworkStatusChange: true,
      ...restOptions,
    })

    // Read at call time via ref so `loadQuery` stays referentially stable
    // (like the raw `execute`) even when callers pass inline objects.
    const executeDefaultsRef = useRef({ queryVariables, errorLog, context })
    useEffect(() => {
      executeDefaultsRef.current = { queryVariables, errorLog, context }
    })

    // Merge our defaults into whatever the caller passes: `variables` and
    // `context` are overridden with the merged values, every other execute
    // option (fetchPolicy, etc.) passes through untouched — mirrors `mutate`.
    // `variables`/`context` are redeclared (not picked from the exec type) so
    // they don't carry the exec option's deprecation markers — we already do
    // the recommended thing (pass context to `execute`, not to the hook).
    const loadQuery = useCallback(
      (
        executeOptions?: Omit<NonNullable<Parameters<typeof execute>[0]>, 'variables' | 'context'> & {
          variables?: Partial<TVariables[TQueryName]>
          context?: DefaultContext
        }
      ) => {
        const defaults = executeDefaultsRef.current

        return execute({
          ...executeOptions,
          variables: { ...defaults.queryVariables, ...executeOptions?.variables } as TVariables[TQueryName],
          context: { errorLog: defaults.errorLog, ...defaults.context, ...executeOptions?.context },
        })
      },
      [execute]
    )

    return [
      loadQuery,
      {
        data,
        loading,
        called,
        error,
        networkStatus,
        refetch,
        fetchMore,
      },
    ] as const
  }
}

/**
 * Builds a mutation hook for the provided mutations.
 *
 * @param MUTATIONS - A record of mutation names to GraphQL DocumentNodes. @see [graphql folder](../../graphql/brands/index.ts)
 * @returns A function that returns a mutation hook for the specified mutation name.
 */
export const buildMutationHook = <
  TNames extends string,
  TVariables extends Record<TNames, Record<string, unknown>>,
  TData extends Record<TNames, unknown>,
>(
  MUTATIONS: Record<TNames, DocumentNode>
) => {
  /**
   * @remarks This hook allows you to execute a mutation and returns a function to call the mutation along with its result.
   * It handles errors and provides loading and called states.
   *
   * @example
   * const [createBrand, { data, loading, error }] = useBrandsMutations({
   *   mutationName: 'createBrand',
   *   mutationVariables: { name: 'New Brand' },
   *   errorLog: CREATE_BRAND_ERROR_LOG,
   * });
   */
  return <TMutationName extends TNames>({
    mutationName,
    mutationVariables,
    errorLog,
    options = {},
  }: MutationHookParamsWithCustomOptions<TMutationName, TVariables[TMutationName], TData[TMutationName]>) => {
    // `context` is kept out of the hook options: mutations only run through
    // `mutate`, which always sends a context — a hook-level one would be
    // silently replaced (Apollo replaces, not merges, context on execute).
    const { context, ...restOptions } = options

    const [execute, { data, loading, called, error }] = useMutation<TData[TMutationName], TVariables[TMutationName]>(
      MUTATIONS[mutationName],
      {
        variables: mutationVariables as TVariables[TMutationName],
        ...restOptions,
      }
    )

    // Read at call time via ref so `mutate` stays referentially stable
    // (like the raw `execute`) even when callers pass inline objects.
    const executeDefaultsRef = useRef({ errorLog, context })
    useEffect(() => {
      executeDefaultsRef.current = { errorLog, context }
    })

    const mutate = useCallback<typeof execute>(
      executeOptions => {
        const defaults = executeDefaultsRef.current

        return execute({
          ...executeOptions,
          context: { errorLog: defaults.errorLog, ...defaults.context, ...executeOptions?.context },
        })
      },
      [execute]
    )

    return [
      mutate,
      {
        data,
        loading,
        called,
        error,
      },
    ] as const
  }
}
